'use client';
/* eslint-disable tailwindcss/no-arbitrary-value */
import {
  formatRangeTitle,
  FormatRangeTitleStaticText,
} from '../../../components/RewardAsideModal/RewardAsideModal';
import { CmsOffer, GiftListItem } from '../../../types/cmsProduct';
import { currencyFormat } from '../../../utils/helpers/formatters';
import { currencyFormatterNoDecimal } from '../../../utils/number';

export type SingSaverRewardPLTranslations = FormatRangeTitleStaticText & {
  plTitle1: string;
  plTitle2: string;
  chooseSsReward: string;
};

interface SingSaverRewardPLProps {
  offers: CmsOffer[];
  staticText: SingSaverRewardPLTranslations;
  providerName: string;
  siteName: string;
  itemNumber: number | null;
  bulletColor: string;
  loanAmount: number;
  loanTenure: number;
}

const isInRange = (
  value: number,
  criteria: { value_start?: string; value_end?: string },
): boolean => {
  const min = Number(criteria.value_start) || 0;
  const max = Number(criteria.value_end) || Infinity;
  return value >= min && value <= max;
};

const matchesLoanCriteria = (
  gift: GiftListItem,
  amount?: number,
  tenure?: number,
): boolean => {
  if (!amount || !tenure || !gift.criteria_list?.length) return false;
  const amountCriteria = gift.criteria_list.find(
    (c) => c.name?.name === 'ELIGIBLE_LOAN_RANGE',
  );
  const tenureCriteria = gift.criteria_list.find(
    (c) => c.name?.name === 'ELIGIBLE_TENURE_RANGE',
  );
  const amountMatch = !amountCriteria || isInRange(amount, amountCriteria);
  const tenureMatch = !tenureCriteria || isInRange(tenure, tenureCriteria);
  return amountMatch && tenureMatch;
};

export const SingSaverRewardPL: React.FC<SingSaverRewardPLProps> = ({
  offers,
  staticText,
  providerName,
  siteName,
  itemNumber,
  bulletColor,
  loanAmount,
  loanTenure,
}) => {
  const allGifts = offers.flatMap((offer) => offer.list_of_gifts ?? []);

  if (allGifts.length === 0) return null;

  const matchedGifts = allGifts.filter((g) =>
    matchesLoanCriteria(g, loanAmount, loanTenure),
  );
  const otherGifts = allGifts.filter(
    (g) => !matchesLoanCriteria(g, loanAmount, loanTenure),
  );

  const renderGiftLine = (gift: GiftListItem) => {
    const giftTitle = gift.gift?.gift_title ?? '';
    const giftValue = gift.gift?.gift_value;
    const prefix = currencyFormat(giftValue?.prefix ?? '');
    const price = giftValue?.price
      ? currencyFormatterNoDecimal.format(giftValue.price)
      : '';
    const criteriaText = formatRangeTitle(
      prefix,
      gift.criteria_list,
      staticText,
    );

    return (
      <div
        key={gift.gift?.uid}
        className="flex items-start gap-spacing/gap/soft-6px"
      >
        <span className="w-[18px] shrink-0 text-center font-font-weight/bullet-1 text-font-size/bullet-1 leading-line-height/bullet-1">
          •
        </span>
        <span className="text-font-size/body-2">
          <span className="font-semibold leading-line-height/bullet-2">
            {`${giftTitle || `${prefix} ${price}`.trim()}`}
          </span>
          {criteriaText && (
            <span className="font-medium leading-line-height/bullet-2 text-text/secondary">
              {` (${criteriaText})`}
            </span>
          )}
        </span>
      </div>
    );
  };

  const formattedAmount = loanAmount
    ? `${currencyFormat(allGifts[0]?.gift?.gift_value?.prefix ?? '')}${currencyFormatterNoDecimal.format(loanAmount)}`
    : '';
  const title1 = staticText.plTitle1
    ?.replace('{providerName}', providerName)
    .replace('{amount}', formattedAmount)
    .replace('{tenure}', String(loanTenure ?? ''))
    .replace('{siteName}', siteName);

  return (
    <div className="flex flex-col gap-spacing/gap/soft-8px">
      {itemNumber != null && (
        <h3 className="font-font-weight/title-2 text-font-size/title-2 leading-line-height/title-2 text-text/secondary">
          {`${itemNumber}. ${staticText.chooseSsReward?.replace(/^\d+\.\s*/, '')}`}
        </h3>
      )}
      {matchedGifts.length > 0 && (
        <>
          <p className="font-font-weight/body-2 text-font-size/body-2 leading-line-height/body-2-relaxed text-text/primary">
            {title1}
          </p>
          <div
            className={`flex flex-col gap-spacing/gap/soft-12px ${bulletColor}`}
          >
            {matchedGifts.map(renderGiftLine)}
          </div>
        </>
      )}

      {otherGifts.length > 0 && (
        <>
          <p className="font-font-weight/body-2 text-font-size/body-2 leading-line-height/body-2-relaxed text-text/primary">
            {staticText.plTitle2}
          </p>
          <div
            className={`flex flex-col gap-spacing/gap/soft-12px ${bulletColor}`}
          >
            {otherGifts.map(renderGiftLine)}
          </div>
        </>
      )}
    </div>
  );
};
